import React from 'react';

function ExamPaperModal({ examData, onClose }) {
  const generateDocx = async () => {
    if (!examData?.meta || !examData?.paper) return;

    const { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } = await import('docx');
    
    const doc = new Document({
      sections: [{
        properties: {},
        children: [
          // Header
          new Paragraph({
            text: examData.meta.subject,
            heading: HeadingLevel.HEADING_1,
            alignment: AlignmentType.CENTER,
            spacing: { after: 300 }
          }),
          new Paragraph({
            text: examData.meta.chapter_title,
            heading: HeadingLevel.HEADING_2,
            alignment: AlignmentType.CENTER,
            spacing: { after: 300 }
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: 'Time: 2 Hours',
                bold: true
              }),
              new TextRun({
                text: '\t\t\t\t\t',
              }),
              new TextRun({
                text: `Max. Marks: ${examData.meta.total_marks}`,
                bold: true
              })
            ],
            alignment: AlignmentType.CENTER,
            spacing: { after: 300 }
          }),

          // Instructions
          new Paragraph({
            text: 'Instructions:',
            heading: HeadingLevel.HEADING_3,
            spacing: { before: 400, after: 200 }
          }),
          ...['Answer all questions.', 
             'Choose any one question from Section B (SAQ).',
             'Each question carries marks as indicated.'
            ].map(text => new Paragraph({
              text: `• ${text}`,
              indent: { left: 400 },
              spacing: { after: 100 }
            })),

          // Section A - VSAQ
          new Paragraph({
            text: 'Section A - Very Short Answer Questions (5 × 1 = 5 Marks)',
            heading: HeadingLevel.HEADING_3,
            spacing: { before: 400, after: 200 }
          }),
          new Paragraph({
            text: 'Answer all questions.',
            italics: true,
            spacing: { after: 200 }
          }),
          ...(examData.paper.section_a_vsaq?.map((q, i) => 
            new Paragraph({
              text: `${i + 1}. ${q.question} (${q.marks} mark${q.marks > 1 ? 's' : ''})`,
              indent: { left: 200 },
              spacing: { after: 150 }
            })
          ) || []),

          // Section B - SAQ
          new Paragraph({
            text: 'Section B - Short Answer Questions (1 × 4 = 4 Marks)',
            heading: HeadingLevel.HEADING_3,
            spacing: { before: 400, after: 200 }
          }),
          new Paragraph({
            text: 'Answer any one question.',
            italics: true,
            spacing: { after: 200 }
          }),
          ...(examData.paper.section_b_saq?.map((q, i) => 
            new Paragraph({
              text: `${i + 1}. ${q.question} (${q.marks} marks)`,
              indent: { left: 200 },
              spacing: { after: 150 }
            })
          ) || []),

          // Section C - LAQ
          new Paragraph({
            text: 'Section C - Long Answer Question (1 × 8 = 8 Marks)',
            heading: HeadingLevel.HEADING_3,
            spacing: { before: 400, after: 200 }
          }),
          new Paragraph({
            text: 'Answer the following question.',
            italics: true,
            spacing: { after: 200 }
          }),
          ...(examData.paper.section_c_laq?.map((q, i) => 
            new Paragraph({
              text: `${i + 1}. ${q.question} (${q.marks} marks)`,
              indent: { left: 200 },
              spacing: { after: 150 }
            })
          ) || []),

          // Section D - MCQ
          new Paragraph({
            text: 'Section D - Multiple Choice Questions (8 × 1 = 8 Marks)',
            heading: HeadingLevel.HEADING_3,
            spacing: { before: 400, after: 200 }
          }),
          new Paragraph({
            text: 'Choose the correct answer for each question.',
            italics: true,
            spacing: { after: 200 }
          }),
          ...(examData.paper.section_d_mcq?.flatMap((q, i) => [
            new Paragraph({
              text: `${i + 1}. ${q.question}`,
              indent: { left: 200 },
              spacing: { after: 100 }
            }),
            ...Object.entries(q.options).map(([key, value]) => 
              new Paragraph({
                text: `   ${key}) ${value}`,
                indent: { left: 400 },
                spacing: { after: 50 }
              })
            )
          ]) || [])
        ]
      }]
    });

    // Generate the document and trigger download
    const blob = await Packer.toBlob(doc);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${examData.meta.subject}_${examData.meta.chapter_title.replace(/\s+/g, '_')}_Exam.docx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white p-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">Exam Paper Preview</h2>
          <div className="flex items-center space-x-4">
            <button
              onClick={generateDocx}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <span>📄</span> Download Word Document
            </button>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 p-1"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        <div className="p-6">
          <div className="prose max-w-none">
            <ExamPaperContent examData={examData} />
          </div>
        </div>
      </div>
    </div>
  );
}

const ExamPaperContent = ({ examData }) => {
  if (!examData?.meta || !examData?.paper) {
    return <div className="text-center p-8 text-red-500">Invalid exam data format</div>;
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold mb-2">{examData.meta.subject}</h1>
        <h2 className="text-xl font-semibold mb-4">{examData.meta.chapter_title}</h2>
        <div className="flex justify-center gap-8 mb-4">
          <span>Time: 2 Hours</span>
          <span>Max. Marks: {examData.meta.total_marks}</span>
        </div>
        <div className="text-left border-t border-b border-gray-200 py-4 my-4">
          <p className="font-semibold mb-2">Instructions:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Answer all questions.</li>
            <li>Choose any one question from Section B (SAQ).</li>
            <li>Each question carries marks as indicated.</li>
          </ul>
        </div>
      </div>

      {/* Section A - VSAQ */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-2">Section A - Very Short Answer Questions (5 × 1 = 5 Marks)</h3>
        <p className="text-sm text-gray-600 mb-4">Answer all questions.</p>
        
        <div className="space-y-4">
          {examData.paper.section_a_vsaq?.map((question, index) => (
            <div key={index} className="flex">
              <span className="font-medium mr-2">{index + 1}.</span>
              <div>
                <span>{question.question}</span>
                <span className="ml-2 text-sm text-gray-500">({question.marks} mark)</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Section B - SAQ */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-2">Section B - Short Answer Questions (1 × 4 = 4 Marks)</h3>
        <p className="text-sm text-gray-600 mb-4">Answer any one question.</p>
        
        <div className="space-y-4">
          {examData.paper.section_b_saq?.map((question, index) => (
            <div key={index} className="flex">
              <span className="font-medium mr-2">{index + 1}.</span>
              <div>
                <span>{question.question}</span>
                <span className="ml-2 text-sm text-gray-500">({question.marks} marks)</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Section C - LAQ */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-2">Section C - Long Answer Question (1 × 8 = 8 Marks)</h3>
        <p className="text-sm text-gray-600 mb-4">Answer the following question.</p>
        
        <div className="space-y-4">
          {examData.paper.section_c_laq?.map((question, index) => (
            <div key={index} className="flex">
              <span className="font-medium mr-2">{index + 1}.</span>
              <div>
                <span>{question.question}</span>
                <span className="ml-2 text-sm text-gray-500">({question.marks} marks)</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Section D - MCQ */}
      <div>
        <h3 className="text-lg font-semibold mb-2">Section D - Multiple Choice Questions (8 × 1 = 8 Marks)</h3>
        <p className="text-sm text-gray-600 mb-4">Choose the correct answer for each question.</p>
        
        <div className="space-y-6">
          {examData.paper.section_d_mcq?.map((question, index) => (
            <div key={index} className="space-y-2">
              <div className="flex">
                <span className="font-medium mr-2">{index + 1}.</span>
                <span>{question.question}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 pl-6">
                {Object.entries(question.options).map(([key, value]) => (
                  <div key={key} className="flex items-center">
                    <span className="font-mono mr-2">({key})</span>
                    <span>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ExamPaperModal;